/* eslint-disable no-nested-ternary */
/* eslint-disable no-unsafe-optional-chaining */
import EndCallWarningModal from '@brands/Dashboard/Dashboard/Components/ConfirmCloseModal/ConfirmCloseModal';
import { useVideoContext, useWindowSize } from '@brands/hooks';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { UserProfile, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectVideoCallCredentials } from '@brands/store/selectors/videoCallCredentials';
import { setShowVideoThumbnail } from '@brands/store/slices/showVideoThumbnailSlice';
import {
  setHasJoined,
  setIsVisible,
  setMobileModalHeight,
  setMobileModalMode,
  setUploadFileMethod,
  setVCCaseId,
} from '@brands/store/slices/videoCallCredentialsSlice';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, IconButton, Modal as MuiModal, Paper, Typography } from '@mui/material';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { ConnectOptions, RemoteParticipant } from 'twilio-video';

import { VideoProvider } from '../../../../contexts/VideoProvider';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useReduxHook';
import useConnectionOptions from '../../../../hooks/video-hooks/useConnectionOptions';
import VideoRoom from '../../../../VideoRoom/VideoRoom/VideoRoom';
import {
  dispatchVideoParticipantPresenceEvent,
  forgetDisconnectedVideoParticipants,
  rememberDisconnectedVideoParticipants,
} from '../../utils/videoRoomParticipants';
import styles from '../../styles.module.scss';
import Panel from './Panel';

const MOBILE_TOP_OFFSET = 72;
const MOBILE_COLLAPSED_HEIGHT = 120;
const MOBILE_COMPACT_HEIGHT = 220;
const MOBILE_EXPANDED_MAX_HEIGHT = 700;
const MOBILE_SNAP_THRESHOLD = 80;
const MOBILE_GRIP_HEIGHT = 28;
const MOBILE_HEADER_HEIGHT = 64;
const MOBILE_BOTTOM_CARD_HEIGHT = 92;
const MOBILE_FULLSCREEN_DRAG_THRESHOLD = 60;
const MOBILE_HIDE_DRAG_THRESHOLD = 60;

const ProviderRoomEffects = ({
  userRoleName,
  hasJoinedFlag,
  appointmentStartTime,
  patientId,
  caseId,
  hasPatientAttended,
  onPatientInRoomChange,
  onShowNoShowBannerChange,
}: {
  userRoleName: UserRoleName;
  hasJoinedFlag: boolean;
  appointmentStartTime: Date | null;
  patientId?: number;
  caseId?: number | string;
  hasPatientAttended: boolean;
  onPatientInRoomChange: (val: boolean) => void;
  onShowNoShowBannerChange: (val: boolean) => void;
}): null => {
  const { room } = useVideoContext();
  const [isPatientInRoomLocal, setIsPatientInRoomLocal] = useState(false);

  useEffect(() => {
    if (!room) {
      setIsPatientInRoomLocal(false);
      onPatientInRoomChange(false);
      return;
    }

    const checkPatient = (participant: RemoteParticipant) =>
      Boolean(patientId) && Number(participant.identity) === Number(patientId);
    const syncParticipantPresence = (
      command: 'participant_connected' | 'participant_disconnected',
      participant: RemoteParticipant,
    ): void => {
      if (!caseId || !participant.identity) return;

      const normalizedCaseId = Number(caseId);
      if (!Number.isFinite(normalizedCaseId)) return;

      if (command === 'participant_connected') {
        forgetDisconnectedVideoParticipants(normalizedCaseId, [participant.identity]);
      } else {
        rememberDisconnectedVideoParticipants(normalizedCaseId, [participant.identity]);
      }

      dispatchVideoParticipantPresenceEvent({
        caseId: normalizedCaseId,
        command,
        participantIdentities: [participant.identity],
      });
    };

    const patientConnected = patientId ? Array.from(room.participants.values()).some(checkPatient) : false;
    setIsPatientInRoomLocal(patientConnected);
    onPatientInRoomChange(patientConnected);

    const participantConnected = (participant: RemoteParticipant) => {
      syncParticipantPresence('participant_connected', participant);

      if (checkPatient(participant)) {
        setIsPatientInRoomLocal(true);
        onPatientInRoomChange(true);
      }
    };

    const participantDisconnected = (participant: RemoteParticipant) => {
      syncParticipantPresence('participant_disconnected', participant);

      if (checkPatient(participant)) {
        setIsPatientInRoomLocal(false);
        onPatientInRoomChange(false);
      }
    };

    room.on('participantConnected', participantConnected);
    room.on('participantDisconnected', participantDisconnected);

    return () => {
      room.off('participantConnected', participantConnected);
      room.off('participantDisconnected', participantDisconnected);
    };
  }, [room, patientId, caseId, onPatientInRoomChange]);

  useEffect(() => {
    // The 10-minute rule only compensates waiting on a patient who never
    // shows. Once the patient has been on the call, the banner must stay
    // away even if they drop off before the provider hangs up.
    if (
      userRoleName !== UserRoleName.Provider ||
      !hasJoinedFlag ||
      isPatientInRoomLocal ||
      hasPatientAttended ||
      !appointmentStartTime
    ) {
      onShowNoShowBannerChange(false);
      return;
    }

    const TEN_MINUTES = 10 * 60 * 1000;
    const startMs = new Date(appointmentStartTime).getTime();

    const updateBannerVisibility = () => {
      const elapsed = Date.now() - startMs;
      onShowNoShowBannerChange(elapsed >= 0 && elapsed <= TEN_MINUTES);
    };

    updateBannerVisibility();
    const interval = window.setInterval(updateBannerVisibility, 30_000);

    return () => window.clearInterval(interval);
  }, [
    userRoleName,
    hasJoinedFlag,
    isPatientInRoomLocal,
    hasPatientAttended,
    appointmentStartTime,
    onShowNoShowBannerChange,
  ]);

  return null;
};

const VideoModal = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);
  const { is_visible: isVisible, uploadFileMethod, vc_case_id: vcCaseId } = useAppSelector(selectVideoCallCredentials);

  const screenSize = useWindowSize();
  const isMobile = screenSize.width < 768;
  const connectionOptions: ConnectOptions = useConnectionOptions();

  const [showEndCallWarning, setShowEndCallWarning] = useState(false);
  const [appointmentStartTime, setAppointmentStartTime] = useState<Date | null>(null);
  const [patient, setPatient] = useState<UserProfile>();
  const [hasJoinedFlag, setHasJoinedFlag] = useState(false);
  const [endCurrentCall, setEndCurrentCall] = useState(false);
  const [isPatientInRoom, setIsPatientInRoom] = useState(false);
  const [hasPatientAttended, setHasPatientAttended] = useState(false);
  const [showNoShowBanner, setShowNoShowBanner] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);

  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [mobileHeight, setMobileHeight] = useState(MOBILE_COLLAPSED_HEIGHT);

  const [nowTs, setNowTs] = useState<number>(Date.now());
  const [joinedAtTs, setJoinedAtTs] = useState<number | null>(null);

  const dragRef = useRef<HTMLDivElement | null>(null);
  const [bounds, setBounds] = useState({ left: 0, top: 0, right: 0, bottom: 0 });

  const dragStartYRef = useRef<number | null>(null);
  const dragStartHeightRef = useRef<number>(MOBILE_COLLAPSED_HEIGHT);

  const onDragStart = useCallback((_e: any, uiData: any) => {
    const { clientWidth, clientHeight } = document.documentElement;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;

    setBounds({
      left: -rect.left + uiData.x,
      right: clientWidth - (rect.right - uiData.x),
      top: -rect.top + uiData.y,
      bottom: clientHeight - (rect.bottom - uiData.y),
    });
  }, []);

  const handleKeyDown = (event: any): void => {
    if (event.key === 'Tab' || event.key === ' ') event.preventDefault();
  };

  const mobileExpandedHeight = useMemo(() => {
    if (typeof window === 'undefined') return MOBILE_EXPANDED_MAX_HEIGHT;
    return Math.min(MOBILE_EXPANDED_MAX_HEIGHT, window.innerHeight - MOBILE_TOP_OFFSET - 16);
  }, [screenSize.height]);

  useEffect(() => {
    if (!isVisible) {
      setEndCurrentCall(false);
      setHasJoinedFlag(false);
      setIsFullScreen(false);
      setIsMobileExpanded(false);
      setMobileHeight(MOBILE_COLLAPSED_HEIGHT);
      setShowEndCallWarning(false);
      setShowNoShowBanner(false);
      setIsPatientInRoom(false);
      setHasPatientAttended(false);
      setNowTs(Date.now());

      dispatch(setMobileModalMode('hidden'));
      dispatch(setMobileModalHeight(0));
    }
  }, [isVisible, dispatch]);

  useEffect(() => {
    if (isVisible && isMobile && !isFullScreen) {
      setIsMobileExpanded(true);
      setMobileHeight(mobileExpandedHeight);

      dispatch(setMobileModalMode('expanded'));
      dispatch(setMobileModalHeight(mobileExpandedHeight));
    }
  }, [isVisible, isMobile, isFullScreen, mobileExpandedHeight, dispatch]);

  useEffect(() => {
    if (endCurrentCall) setHasJoinedFlag(false);
  }, [endCurrentCall]);

  useEffect(() => {
    if (!isMobile || !isVisible || isFullScreen) return;

    dispatch(setMobileModalHeight(mobileHeight));
  }, [mobileHeight, isMobile, isVisible, isFullScreen, dispatch]);

  useEffect(() => {
    if (!isMobile || !isVisible) return;

    if (isFullScreen) {
      dispatch(setMobileModalMode('fullscreen'));
      return;
    }

    if (mobileHeight >= mobileExpandedHeight) {
      dispatch(setMobileModalMode('expanded'));
    } else if (mobileHeight >= MOBILE_COMPACT_HEIGHT) {
      dispatch(setMobileModalMode('compact'));
    } else {
      dispatch(setMobileModalMode('collapsed'));
    }
  }, [isMobile, isVisible, isFullScreen, isMobileExpanded, mobileHeight, mobileExpandedHeight, dispatch]);

  useEffect(() => {
    if (!vcCaseId) return;

    const fetchCase = async (): Promise<void> => {
      try {
        const currentCase = await getOneCase(Number(vcCaseId));
        setPatient(currentCase.patient);

        if (currentCase.video_conference_date) {
          // Backend sends naive ISO (no Z) — force UTC parse so the
          // appointment-vs-now comparisons below aren't off by the
          // browser's timezone offset.
          setAppointmentStartTime(moment.utc(currentCase.video_conference_date).toDate());
        } else {
          setAppointmentStartTime(null);
        }
      } catch (error) {
        console.error('Failed to fetch case:', error);
      }
    };

    if (userInfo?.role?.name === UserRoleName.Provider) fetchCase();
  }, [vcCaseId, userInfo?.role?.name]);

  // The timer counts from the moment the provider actually connected, not
  // from the scheduled start: providers routinely join early, and the shown
  // time has to match the real time spent on the call (it also backs their
  // expectation of getting paid for that time).
  useEffect(() => {
    if (hasJoinedFlag) {
      setJoinedAtTs((prev) => prev ?? Date.now());
    } else {
      setJoinedAtTs(null);
    }
  }, [hasJoinedFlag]);

  useEffect(() => {
    if (!joinedAtTs) return;

    setNowTs(Date.now());

    const id = window.setInterval(() => {
      setNowTs(Date.now());
    }, 1000);

    return () => window.clearInterval(id);
  }, [joinedAtTs]);

  useEffect(() => {
    dispatch(setHasJoined(hasJoinedFlag));
  }, [hasJoinedFlag]);

  const elapsedSeconds = useMemo((): number => {
    if (!joinedAtTs) return 0;

    const diffInSeconds = Math.floor((nowTs - joinedAtTs) / 1000);
    return diffInSeconds > 0 ? diffInSeconds : 0;
  }, [nowTs, joinedAtTs]);

  const formattedTimer = useMemo((): string => {
    const mm = String(Math.floor(elapsedSeconds / 60)).padStart(2, '0');
    const ss = String(elapsedSeconds % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [elapsedSeconds]);

  // Latch attendance for the whole call session: presence flips back to
  // false when the patient drops, but "the patient was seen" must survive
  // that for the no-show banner and the end-call payment warning.
  const handlePatientInRoomChange = useCallback((inRoom: boolean): void => {
    setIsPatientInRoom(inRoom);
    if (inRoom) setHasPatientAttended(true);
  }, []);

  const endCallNow = (): void => {
    setEndCurrentCall(true);
    dispatch(setIsVisible(false));
    dispatch(setVCCaseId(''));
    dispatch(setShowVideoThumbnail(false));
    dispatch(setUploadFileMethod(() => Promise.resolve()));
    localStorage.setItem('backgroundSettings', JSON.stringify({ type: 'none', index: 0 }));
  };

  const handleMobileGripPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    dragStartYRef.current = event.clientY;
    dragStartHeightRef.current = mobileHeight;
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleMobileGripPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (dragStartYRef.current === null) return;

    const deltaY = event.clientY - dragStartYRef.current;
    const nextHeight = dragStartHeightRef.current + deltaY;

    // Allow dragging past expanded (towards fullscreen) and below collapsed (towards hide)
    setMobileHeight(Math.max(0, Math.min(mobileExpandedHeight + MOBILE_FULLSCREEN_DRAG_THRESHOLD, nextHeight)));
  };

  const handleMobileGripPointerEnd = (): void => {
    if (dragStartYRef.current === null) return;
    dragStartYRef.current = null;

    const h = mobileHeight;

    if (h >= mobileExpandedHeight + MOBILE_FULLSCREEN_DRAG_THRESHOLD / 2) {
      // Snap → fullscreen
      setIsFullScreen(true);
      setIsMobileExpanded(true);
      setMobileHeight(mobileExpandedHeight);
      dispatch(setMobileModalMode('fullscreen'));
    } else if (h >= (mobileExpandedHeight + MOBILE_COMPACT_HEIGHT) / 2) {
      // Snap → expanded
      setIsMobileExpanded(true);
      setMobileHeight(mobileExpandedHeight);
    } else if (h >= (MOBILE_COMPACT_HEIGHT + MOBILE_COLLAPSED_HEIGHT) / 2) {
      // Snap → compact
      setIsMobileExpanded(true);
      setMobileHeight(MOBILE_COMPACT_HEIGHT);
    } else if (h >= MOBILE_COLLAPSED_HEIGHT - MOBILE_HIDE_DRAG_THRESHOLD) {
      // Snap → mini / collapsed
      setIsMobileExpanded(false);
      setMobileHeight(MOBILE_COLLAPSED_HEIGHT);
    } else {
      // Snap → hide (dismiss)
      setIsMobileExpanded(false);
      setMobileHeight(MOBILE_COLLAPSED_HEIGHT);
      dispatch(setIsVisible(false));
    }
  };

  const VideoContent = (
    <VideoRoom
      layout={isFullScreen ? 'full' : 'mini'}
      endCurrentCall={endCurrentCall}
      uploadFile={uploadFileMethod}
      setHasJoinedFlag={setHasJoinedFlag}
      alreadyJoined={hasJoinedFlag}
      appointmentStartTime={appointmentStartTime}
      isPatientInRoom={isPatientInRoom}
      hasPatientAttended={hasPatientAttended}
    />
  );

  const headerTitle = patient?.full_name || 'Video call';

  const InnerPaper = (
    <Paper
      elevation={12}
      onKeyDown={handleKeyDown}
      className={styles.wrapper}
      sx={{
        width: isMobile ? '100vw' : isFullScreen ? 'calc(100vw - 64px)' : 299,
        maxWidth: isMobile ? '100vw' : isFullScreen ? 'none' : 299,
        height: isMobile
          ? isFullScreen
            ? '100dvh'
            : `${mobileHeight}px`
          : isFullScreen
          ? 'calc(100vh - 64px)'
          : 'fit-content',
        borderRadius: isMobile ? 0 : 4,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        padding: 0,
        boxShadow: isMobile && !isFullScreen ? '0 2px 14px rgba(0, 0, 0, 0.18)' : undefined,
      }}
    >
      <Box
        sx={{
          px: isMobile ? 2 : isFullScreen ? 2 : 1,
          py: isMobile ? 1.25 : isFullScreen ? 1.5 : 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: '1px solid rgba(230, 234, 240, 1)',
          bgcolor: 'background.paper',
          flex: '0 0 auto',
          paddingTop: isMobile ? (isFullScreen ? '8px' : 'unset') : isFullScreen ? '16px' : 'unset',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {!isMobile && !isFullScreen && (
            <Box
              className="video-drag-handle"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 34,
                height: 34,
                borderRadius: 2,
                cursor: 'grab',
                color: 'text.secondary',
                '&:active': { cursor: 'grabbing' },
                userSelect: 'none',
                flex: '0 0 auto',
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
          )}

          <Typography
            noWrap
            sx={{
              fontWeight: 700,
              minWidth: 0,
              fontSize: isMobile ? '14px' : isFullScreen ? '16px' : '12px',
            }}
          >
            {headerTitle}
          </Typography>

          {hasJoinedFlag && appointmentStartTime && (
            <Typography
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                flex: '0 0 auto',
                fontSize: isMobile ? '13px' : isFullScreen ? '16px' : '12px',
              }}
            >
              {formattedTimer}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: '0 0 auto' }}>
          <IconButton
            onClick={() => {
              const nextValue = !isFullScreen;
              setIsFullScreen(nextValue);

              if (isMobile) {
                if (nextValue) {
                  dispatch(setMobileModalMode('fullscreen'));
                } else {
                  dispatch(setMobileModalMode(mobileHeight >= MOBILE_COMPACT_HEIGHT ? 'expanded' : 'collapsed'));
                  dispatch(setMobileModalHeight(mobileHeight));
                }
              }
            }}
            size="small"
            aria-label={isFullScreen ? 'Minimize video' : 'Full screen'}
          >
            {isFullScreen ? <CloseFullscreenIcon fontSize="small" /> : <FitScreenIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
        {showNoShowBanner && (
          <Box
            sx={{
              position: 'absolute',
              left: 8,
              right: 8,
              top: 8,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              bgcolor: '#fff7e6',
              color: '#8a5700',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              fontSize: 12,
              lineHeight: 1.3,
            }}
          >
            <WarningIcon fontSize="small" />
            <span>
              Please note: To get paid for this consultation, you must remain on the call for a minimum of 10 minutes,
              even if your patient no-showed.
            </span>
          </Box>
        )}

        {VideoContent}
      </Box>
    </Paper>
  );

  const MobileWaitingRoomSheet = (
    <Box
      sx={{
        position: 'fixed',
        top: `${MOBILE_TOP_OFFSET}px`,
        left: 0,
        right: 0,
        zIndex: 1400,
        height: `${mobileHeight}px`,
        transition: dragStartYRef.current === null ? 'height 220ms ease' : 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        // No bottom rounding — the video section connects flush to the app header above.
        // The bottom section below carries rounded top corners instead.
        boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
      }}
    >
      {/* Video / waiting room content — shown only when expanded */}
      {mobileHeight > MOBILE_COLLAPSED_HEIGHT && (
        <Box
          sx={{
            position: 'relative',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          {showNoShowBanner && (
            <Box
              sx={{
                position: 'absolute',
                left: 8,
                right: 8,
                top: 8,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.25,
                py: 1,
                borderRadius: 2,
                bgcolor: '#fff7e6',
                color: '#8a5700',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                fontSize: 12,
                lineHeight: 1.3,
              }}
            >
              <WarningIcon fontSize="small" />
              <span>
                Please note: To get paid for this consultation, you must remain on the call for a minimum of 10 minutes,
                even if your patient no-showed.
              </span>
            </Box>
          )}

          {VideoContent}
        </Box>
      )}

      {/* Bottom section — always visible.
          Rounded top corners mark this as a pull-up drawer beneath the video frame. */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: '#fff',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
        }}
      >
        {/* Drag grip sits at the top of the bottom section so users can resize */}
        <Box
          onPointerDown={handleMobileGripPointerDown}
          onPointerMove={handleMobileGripPointerMove}
          onPointerUp={handleMobileGripPointerEnd}
          onPointerCancel={handleMobileGripPointerEnd}
          sx={{
            height: MOBILE_GRIP_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            touchAction: 'none',
            cursor: 'ns-resize',
          }}
        >
          <Box sx={{ width: 40, height: 6, borderRadius: 999, bgcolor: '#B8B8B8' }} />
        </Box>

        {/* Patient / provider info card — shown only when expanded */}
        {mobileHeight > MOBILE_COLLAPSED_HEIGHT && (
          <Box
            sx={{
              px: 2,
              pt: 0,
              pb: 'calc(16px + env(safe-area-inset-bottom))',
              minHeight: MOBILE_BOTTOM_CARD_HEIGHT,
            }}
          >
            <Box
              sx={{
                border: '1px solid #E7B6F0',
                borderRadius: '18px',
                px: 1.5,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  noWrap
                  sx={{
                    fontSize: '16px',
                    lineHeight: '22px',
                    fontWeight: 700,
                    color: '#334155',
                  }}
                >
                  {patient?.full_name}
                </Typography>

                <Typography
                  sx={{
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#475569',
                  }}
                >
                  {hasJoinedFlag && appointmentStartTime ? formattedTimer : ''}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  const mobileFullscreenSheet = (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Video content fills all available space above the drag bar */}
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden', bgcolor: 'white' }}>
        {showNoShowBanner && (
          <Box
            sx={{
              position: 'absolute',
              left: 8,
              right: 8,
              top: 8,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              bgcolor: '#fff7e6',
              color: '#8a5700',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              fontSize: 12,
              lineHeight: 1.3,
            }}
          >
            <WarningIcon fontSize="small" />
            <span>
              Please note: To get paid for this consultation, you must remain on the call for a minimum of 10 minutes,
              even if your patient no-showed.
            </span>
          </Box>
        )}
        {VideoContent}
      </Box>

      {/* Drag bar at bottom — tap or drag down to collapse out of fullscreen */}
      <Box
        onPointerDown={() => {
          setIsFullScreen(false);
          setIsMobileExpanded(true);
          setMobileHeight(mobileExpandedHeight);
        }}
        sx={{
          flexShrink: 0,
          height: MOBILE_GRIP_HEIGHT + 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          touchAction: 'none',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ width: 40, height: 6, borderRadius: 999, bgcolor: '#B8B8B8' }} />
      </Box>
    </Box>
  );

  const desktopDraggableMini = (
    <Draggable nodeRef={dragRef} bounds={bounds} handle=".video-drag-handle" onStart={onDragStart}>
      <div
        ref={dragRef}
        style={{
          position: 'fixed',
          left: 16,
          top: '22%',
          zIndex: 1200,
        }}
      >
        {InnerPaper}
      </div>
    </Draggable>
  );

  const mobilePinnedMini = MobileWaitingRoomSheet;

  const fullscreenContent = (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1500,
        bgcolor: 'rgba(0,0,0,0.45)',
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          inset: isMobile ? 0 : 32,
        }}
      >
        {InnerPaper}
      </Box>
    </Box>
  );

  return (
    <VideoProvider options={connectionOptions}>
      <>
        {/* Mounted outside the layout branches: collapsing the mobile sheet or
            toggling fullscreen must not tear down the room's presence listeners,
            or a patient joining meanwhile would go unrecorded and the no-show
            banner and end-call payment warning would misfire. */}
        {isVisible && (
          <ProviderRoomEffects
            userRoleName={userInfo?.role?.name}
            hasJoinedFlag={hasJoinedFlag}
            appointmentStartTime={appointmentStartTime}
            patientId={patient?.guardian_id ?? patient?.id}
            caseId={vcCaseId}
            hasPatientAttended={hasPatientAttended}
            onPatientInRoomChange={handlePatientInRoomChange}
            onShowNoShowBannerChange={setShowNoShowBanner}
          />
        )}

        {isVisible && !isFullScreen && (isMobile ? mobilePinnedMini : desktopDraggableMini)}

        {/* Mobile fullscreen: custom layout with drag bar at bottom */}
        {isVisible && isFullScreen && isMobile && mobileFullscreenSheet}

        {/* Desktop fullscreen: MuiModal overlay */}
        <MuiModal open={isVisible && isFullScreen && !isMobile} onClose={() => undefined} disableEscapeKeyDown>
          {fullscreenContent}
        </MuiModal>

        <EndCallWarningModal isOpen={showEndCallWarning} toggleModal={setShowEndCallWarning} endCallNow={endCallNow} />
      </>
    </VideoProvider>
  );
};

export default VideoModal;
